import { fromUnixTime } from "date-fns";
import { t } from "i18next";
import { format } from "date-fns";
import { InterviewLocationTypeEnum, SaveInterviewAppointmentBody, CreateInterviewSlotDto, InterviewAppointment, SendInvitationsResponseData, SendInterviewInvitationBody, ScheduledInterviewDto, InterviewInvitationStatusEnum } from '../interfaces/interview-scheduling/InterviewSchedulingTypes';
import { urlRegex } from '../constants/constants';
import { ApplicantListItem } from '../interfaces/applicants/ApplicantListItem';

/**
 * Normalizes a URL by adding https:// if no protocol is present
 * @param url - The URL to normalize
 * @returns The normalized URL with https:// prefix if needed
 */
export const normalizeUrl = (url: string): string => {
    if (!url || !url.trim()) {
        return url;
    }

    const trimmedUrl = url.trim();

    // Check if URL already has a protocol (http://, https://, ftp://, etc.)
    if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(trimmedUrl)) {
        return trimmedUrl;
    }

    // Add https:// if no protocol is present
    return `https://${trimmedUrl}`;
};

/**
 * Validates a URL format (allows URLs without protocol for user input)
 * @param url - The URL to validate
 * @returns true if the URL is valid (with or without protocol)
 */
export const isValidUrlFormat = (url: string): boolean => {
    if (!url || !url.trim()) {
        return false;
    }

    const trimmedUrl = url.trim();

    // Check if URL already has a protocol
    if (urlRegex.test(trimmedUrl)) {
        return true;
    }

    // Check if URL would be valid with https:// prefix
    const urlWithProtocol = `https://${trimmedUrl}`;
    return urlRegex.test(urlWithProtocol);
};

/**
 * Detects the interview location type from a URL string.
 * Handles Zoom (all subdomains), Google Meet, Microsoft Teams, Google Maps, and Apple Maps.
 * 
 * @param locationUrl - The URL to analyze
 * @returns InterviewLocationTypeEnum.ONLINE or InterviewLocationTypeEnum.ONSITE, or null if URL is empty
 */
export const detectLocationTypeFromUrl = (locationUrl: string | null | undefined): InterviewLocationTypeEnum | null => {
    if (!locationUrl || !locationUrl.trim()) {
        return null;
    }

    const url = locationUrl.toLowerCase().trim();

    // Check for Google Maps links (ONSITE)
    // Matches: maps.google.com, google.com/maps, goo.gl/maps, maps.app.goo.gl, plus.google.com/maps
    if (/maps\.google\.com|google\.com\/maps|goo\.gl\/maps|maps\.app\.goo\.gl|plus\.google\.com\/maps/.test(url)) {
        return InterviewLocationTypeEnum.ONSITE;
    }

    // Check for Apple Maps links (ONSITE)
    // Matches: maps.apple.com, apple.com/maps
    if (/maps\.apple\.com|apple\.com\/maps/.test(url)) {
        return InterviewLocationTypeEnum.ONSITE;
    }

    // Check for Zoom links (ONLINE)
    // Matches: zoom.us, *.zoom.us, zoom.com, *.zoom.com, zoomgov.com, *.zoomgov.com
    // Examples: us06web.zoom.us, zoom.us, zoom.com, us02web.zoom.us, etc.
    if (/zoom\.(us|com)|zoomgov\.com/.test(url)) {
        return InterviewLocationTypeEnum.ONLINE;
    }

    // Check for Google Meet links (ONLINE)
    // Matches: meet.google.com, google.com/meet
    if (/meet\.google\.com|google\.com\/meet/.test(url)) {
        return InterviewLocationTypeEnum.ONLINE;
    }

    // Check for Microsoft Teams links (ONLINE)
    // Matches: teams.microsoft.com, teams.live.com, teams.microsoft.us, etc.
    if (/teams\.(microsoft|live)\.(com|us|eu|asia)|microsoft\.com\/teams/.test(url)) {
        return InterviewLocationTypeEnum.ONLINE;
    }

    // Check for other common video conferencing platforms (ONLINE)
    // Webex, GoToMeeting, BlueJeans, etc.
    if (/webex\.com|gotomeeting\.com|bluejeans\.com|skype\.com|hangouts\.google\.com/.test(url)) {
        return InterviewLocationTypeEnum.ONLINE;
    }

    // Default: if URL exists but doesn't match known patterns, assume ONLINE
    // This is safer than assuming ONSITE since most modern interviews are online
    return InterviewLocationTypeEnum.ONSITE;
};

/**
 * Infers the location name from a URL string for online interviews.
 * Returns platform names like "Google Meet", "Zoom", "Microsoft Teams", etc.
 * Supports both English and Arabic based on the current i18next language setting.
 * 
 * @param locationUrl - The URL to analyze
 * @returns The inferred location name (translated), or null if URL is empty or doesn't match known patterns
 */
export const inferLocationNameFromUrl = (locationUrl: string | null | undefined): string | null => {
    if (!locationUrl || !locationUrl.trim()) {
        return null;
    }

    const url = locationUrl.toLowerCase().trim();

    // Check for Zoom links
    if (/zoom\.(us|com)|zoomgov\.com/.test(url)) {
        return t('location_name_zoom');
    }

    // Check for Google Meet links
    if (/meet\.google\.com|google\.com\/meet/.test(url)) {
        return t('location_name_google_meet');
    }

    // Check for Microsoft Teams links
    if (/teams\.(microsoft|live)\.(com|us|eu|asia)|microsoft\.com\/teams/.test(url)) {
        return t('location_name_microsoft_teams');
    }

    // Check for other common video conferencing platforms
    if (/webex\.com/.test(url)) {
        return t('location_name_webex');
    }
    if (/gotomeeting\.com/.test(url)) {
        return t('location_name_gotomeeting');
    }
    if (/bluejeans\.com/.test(url)) {
        return t('location_name_bluejeans');
    }
    if (/skype\.com/.test(url)) {
        return t('location_name_skype');
    }
    if (/hangouts\.google\.com/.test(url)) {
        return t('location_name_google_hangouts');
    }

    // Return null if no match found
    return null;
};

export const formatDate = (dateString: string, isRtl: boolean): string => {
    try {
        const date = new Date(dateString);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dateOnly = new Date(date);
        dateOnly.setHours(0, 0, 0, 0);

        if (dateOnly.getTime() === today.getTime()) {
            return t('today');
        }

        const lang = isRtl ? 'ar' : 'en';
        try {
            return format(date, 'EEE dd MMM', {
                locale: lang === 'ar' ? require('date-fns/locale/ar') : require('date-fns/locale/en-US')
            });
        } catch {
            return format(date, 'EEE dd MMM');
        }
    } catch {
        return dateString;
    }
};

export const getDayfromUnixTimestamp = (timestamp: number, lang: string): string => {
    const date = fromUnixTime(timestamp);
    // Dynamically require locale and ensure it has localize property
    let localeMod;
    if (lang === 'ar') {
        localeMod = require('date-fns/locale/ar');
    } else {
        localeMod = require('date-fns/locale/en-US');
    }
    const locale = localeMod && localeMod.default && localeMod.default.localize
        ? localeMod.default
        : localeMod.localize
            ? localeMod
            : localeMod.default || localeMod; // fallback

    return format(date, 'EEE dd, MMM', { locale });
};

/**
 * Formats interview date in the format: "Sun (21 Jan 2025)" or "Today (20 Jan 2025)" if today
 * Supports both English and Arabic
 * 
 * @param timestamp - Unix timestamp in seconds
 * @param isRtl - Whether the language is RTL (Arabic)
 * @param showDateOnlyForRelativeDays - If true, shows only "Today"/"Yesterday"/"Tomorrow" without date for relative days. Default: false
 * @returns Formatted date string
 */
export const formatInterviewDate = (timestamp: number, isRtl: boolean, showDateOnlyForRelativeDays: boolean = false): string => {
    try {
        const date = fromUnixTime(timestamp);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dateOnly = new Date(date);
        dateOnly.setHours(0, 0, 0, 0);

        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        const lang = isRtl ? 'ar' : 'en';
        let localeMod;
        if (lang === 'ar') {
            localeMod = require('date-fns/locale/ar');
        } else {
            localeMod = require('date-fns/locale/en-US');
        }
        const locale = localeMod && localeMod.default && localeMod.default.localize
            ? localeMod.default
            : localeMod.localize
                ? localeMod
                : localeMod.default || localeMod;

        const isToday = dateOnly.getTime() === today.getTime();
        const isYesterday = dateOnly.getTime() === yesterday.getTime();
        const isTomorrow = dateOnly.getTime() === tomorrow.getTime();

        let dayName = format(date, 'EEE', { locale });
        if (lang === 'ar') {
            dayName = 'ال' + dayName;
        }
        const datePart = format(date, 'dd MMM yyyy', { locale });

        if (showDateOnlyForRelativeDays) {
            // For table column: show only label for Today/Yesterday/Tomorrow
            if (isToday) {
                return t('today');
            } else if (isYesterday) {
                return t('yesterday');
            } else if (isTomorrow) {
                return t('tomorrow');
            }
        } else {
            // Default behavior: show label with date for Today
            if (isToday) {
                const todayText = t('today');
                return `${todayText} (${datePart})`;
            }
        }

        return `${dayName} (${datePart})`;
    } catch (error) {
        console.error('Error formatting interview date:', error);
        return '-';
    }
};

export const getTimeHoursMinsfromUnixTimestamp = (timestamp: number, lang: string): string => {
    const date = fromUnixTime(timestamp);
    let localeMod;
    if (lang === 'ar') {
        localeMod = require('date-fns/locale/ar');
    } else {
        localeMod = require('date-fns/locale/en-US');
    }
    // Ensure we pass an object with a localize property, as required by date-fns
    const locale = localeMod && localeMod.default && localeMod.default.localize
        ? localeMod.default
        : localeMod.localize
            ? localeMod
            : localeMod.default || localeMod;
    // Use 12-hour format with AM/PM
    let formattedTime = format(date, 'h:mm a', { locale });
    // Replace Arabic abbreviations with full words
    if (lang === 'ar') {
        // Replace " ص" with " صباحًا" and " م" with " مساءً"
        formattedTime = formattedTime.replace(/\sص(\s|$)/g, ' صباحًا$1').replace(/\sم(\s|$)/g, ' مساءً$1');
    }
    return formattedTime;
};

/**
 * Formats interview time range in the format: "10:00 to 11:00" or "10:00 - 11:00"
 * Supports both English and Arabic
 * 
 * @param startTime - Unix timestamp in seconds for start time
 * @param endTime - Unix timestamp in seconds for end time
 * @param isRtl - Whether the language is RTL (Arabic)
 * @param useTo - If true, use "to" separator, otherwise use dash "-"
 * @returns Formatted time range string
 */
export const formatInterviewTime = (startTime: number, endTime: number, isRtl: boolean, useTo: boolean = false): string => {
    try {
        const lang = isRtl ? 'ar' : 'en';
        const startTimeStr = getTimeHoursMinsfromUnixTimestamp(startTime, lang);
        const endTimeStr = getTimeHoursMinsfromUnixTimestamp(endTime, lang);

        if (useTo) {
            const toText = t('to');
            return `${startTimeStr} ${toText} ${endTimeStr}`;
        } else {
            return `${startTimeStr} - ${endTimeStr}`;
        }
    } catch (error) {
        console.error('Error formatting interview time:', error);
        return '-';
    }
};

// Helper function to format interview time
export const formatInterviewTimeDisplay = (interview: ScheduledInterviewDto, isRtl: boolean): string => {
    if (!interview.slot) {
        return '';
    }

    // Check if timestamp is in milliseconds (> year 2001 in seconds) or seconds
    // If > 1e12, it's likely milliseconds, otherwise seconds
    const isMilliseconds = interview.slot.startTime > 1e12;
    const startTimeSeconds = isMilliseconds
        ? Math.floor(interview.slot.startTime / 1000)
        : interview.slot.startTime;
    const endTimeSeconds = isMilliseconds
        ? Math.floor(interview.slot.endTime / 1000)
        : interview.slot.endTime;

    const dateStr = formatInterviewDate(startTimeSeconds, isRtl);
    const timeStr = formatInterviewTime(startTimeSeconds, endTimeSeconds, isRtl, false);

    return `${dateStr}, ${timeStr}`;
};

// Helper function to convert date and time strings to Unix timestamp in milliseconds
export const convertToUnixTimestamp = (dateString: string, timeString: string): number => {
    const dateTimeString = `${dateString}T${timeString}:00`;
    const date = new Date(dateTimeString);
    return date.getTime();
};

// Helper function to compare time strings (HH:mm format)
export const compareTimes = (time1: string, time2: string): number => {
    const [hours1, minutes1] = time1.split(':').map(Number);
    const [hours2, minutes2] = time2.split(':').map(Number);
    const totalMinutes1 = hours1 * 60 + minutes1;
    const totalMinutes2 = hours2 * 60 + minutes2;
    return totalMinutes1 - totalMinutes2;
};

/**
 * Calculates the duration in hours between two times
 * @param startTime - Start time in HH:mm format
 * @param endTime - End time in HH:mm format
 * @returns Duration in hours (as a decimal)
 */
export const calculateTimeDuration = (startTime: string, endTime: string): number => {
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [endHours, endMinutes] = endTime.split(':').map(Number);
    const startTotalMinutes = startHours * 60 + startMinutes;
    const endTotalMinutes = endHours * 60 + endMinutes;
    const durationMinutes = endTotalMinutes - startTotalMinutes;
    return durationMinutes / 60; // Convert to hours
};

// Interview scheduling validation and body preparation utilities
export interface TimeSlot {
    id: string;
    date: string;
    startTime: string;
    endTime: string;
}

export interface ValidationErrors {
    locationLink?: string;
    locationName?: string;
    slots?: string;
    slotTimes?: { [slotId: string]: string };
    capacity?: string;
}

export interface ValidateScheduleParams {
    locationLink: string;
    locationName: string;
    timeSlots: TimeSlot[];
    selectedAppointment?: InterviewAppointment | null;
    validateLocation?: boolean;
    validateSlots?: boolean;
    checkSlotsExist?: boolean;
    applicantsPerSlot?: string;
}

export const validateSchedule = (params: ValidateScheduleParams, t: (key: string) => string): { errors: ValidationErrors; isValid: boolean } => {
    const { locationLink, locationName, timeSlots, selectedAppointment, validateLocation = true, validateSlots = true, checkSlotsExist = false, applicantsPerSlot } = params;
    const errors: ValidationErrors = {};

    if (validateLocation) {
        // Validate location link is required
        if (!locationLink.trim()) {
            errors.locationLink = t('location_link_required');
        } else {
            // Validate location link is a valid URL (allows URLs without protocol)
            if (!isValidUrlFormat(locationLink)) {
                errors.locationLink = t('location_link_invalid_url');
            }
        }

        // Detect location type for validation (only if URL is valid)
        // Normalize URL for detection (add https:// if missing)
        let locationType = selectedAppointment?.locationType;
        if (!locationType && locationLink.trim() && isValidUrlFormat(locationLink)) {
            const normalizedUrl = normalizeUrl(locationLink);
            locationType = detectLocationTypeFromUrl(normalizedUrl) || InterviewLocationTypeEnum.ONLINE;
        }

        // Validate location name is required if location type is onsite
        if (locationType === InterviewLocationTypeEnum.ONSITE && !locationName.trim()) {
            errors.locationName = t('location_name_required');
        }
    }

    if (checkSlotsExist) {
        if (!selectedAppointment?.slots || selectedAppointment.slots.length === 0) {
            return { errors, isValid: false };
        }
    }

    if (validateSlots) {
        // Validate at least 1 slot is required with all fields filled
        const validSlots = timeSlots.filter(slot =>
            slot.date && slot.startTime && slot.endTime
        );

        if (validSlots.length === 0) {
            errors.slots = t('at_least_one_slot_required');
        }

        // Validate that all slots are either completely empty or completely filled
        // Show error for incomplete slots (slots with some but not all fields filled)
        const slotTimeErrors: { [slotId: string]: string } = {};
        timeSlots.forEach(slot => {
            const hasDate = !!slot.date;
            const hasStartTime = !!slot.startTime;
            const hasEndTime = !!slot.endTime;
            const isPartiallyFilled = (hasDate || hasStartTime || hasEndTime) && !(hasDate && hasStartTime && hasEndTime);

            if (isPartiallyFilled) {
                slotTimeErrors[slot.id] = t('slot_must_be_complete');
            } else if (slot.date && slot.startTime && slot.endTime) {
                // Validate end time is larger than start time for complete slots
                if (compareTimes(slot.endTime, slot.startTime) <= 0) {
                    slotTimeErrors[slot.id] = t('end_time_must_be_after_start_time');
                } else {
                    // Validate slot duration is not more than 8 hours
                    const duration = calculateTimeDuration(slot.startTime, slot.endTime);
                    if (duration > 8) {
                        slotTimeErrors[slot.id] = t('slot_duration_cannot_exceed_8_hours');
                    } else {
                        // Check if the selected date is today and times are in the past
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);

                        // Parse slot date - slot.date is in YYYY-MM-DD format
                        // Create date in local timezone to avoid UTC issues
                        const [year, month, day] = slot.date.split('-').map(Number);
                        const slotDate = new Date(year, month - 1, day);
                        slotDate.setHours(0, 0, 0, 0);

                        // Check if slot date is today
                        if (slotDate.getTime() === today.getTime()) {
                            const now = new Date();
                            const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

                            // Check if start time or end time is earlier than current time
                            if (compareTimes(slot.startTime, currentTime) < 0 || compareTimes(slot.endTime, currentTime) < 0) {
                                slotTimeErrors[slot.id] = t('time_cannot_be_in_past_for_today');
                            }
                        }
                    }
                }
            }
        });

        // Check for duplicate slots (same date, startTime, and endTime)
        // Only check duplicates for valid (complete) slots
        validSlots.forEach((slot, index) => {
            const duplicateIndex = validSlots.findIndex((s, i) =>
                i !== index &&
                s.date === slot.date &&
                s.startTime === slot.startTime &&
                s.endTime === slot.endTime
            );
            if (duplicateIndex !== -1) {
                // Mark both slots as duplicates (the original and the duplicate)
                slotTimeErrors[slot.id] = t('duplicate_slot_error');
                const duplicateSlot = validSlots[duplicateIndex];
                if (!slotTimeErrors[duplicateSlot.id]) {
                    slotTimeErrors[duplicateSlot.id] = t('duplicate_slot_error');
                }
            }
        });

        if (Object.keys(slotTimeErrors).length > 0) {
            errors.slotTimes = slotTimeErrors;
        }
    }

    // Validate capacity (applicants per slot) - maximum 200
    if (applicantsPerSlot && applicantsPerSlot.trim()) {
        const capacity = parseInt(applicantsPerSlot, 10);
        if (isNaN(capacity) || capacity < 1) {
            errors.capacity = t('capacity_must_be_positive_number');
        } else if (capacity > 200) {
            errors.capacity = t('capacity_cannot_exceed_200');
        }
    }

    return { errors, isValid: Object.keys(errors).length === 0 };
};

/**
 * Updates validation errors for a specific slot based on full validation
 * This is used to check for past time errors that the basic TimeSlotsInput validation doesn't catch
 * 
 * @param slotId - The ID of the slot to validate
 * @param timeSlots - Array of all time slots
 * @param currentErrors - Current validation errors object
 * @param params - Parameters for validation (locationLink, locationName, selectedAppointment, hasCompleteSlot)
 * @param t - Translation function
 * @returns Updated validation errors object
 */
export const getUpdatedSlotValidationErrors = (
    slotId: string,
    timeSlots: TimeSlot[],
    currentErrors: ValidationErrors,
    params: {
        locationLink: string;
        locationName: string;
        selectedAppointment?: InterviewAppointment | null;
        hasCompleteSlot: boolean;
    },
    t: (key: string) => string
): ValidationErrors => {
    const { locationLink, locationName, selectedAppointment, hasCompleteSlot } = params;
    const slot = timeSlots.find(s => s.id === slotId);

    if (slot && slot.date && slot.startTime && slot.endTime) {
        // Re-run validation for this specific slot to check for past time errors
        const validationResult = validateSchedule({
            locationLink,
            locationName,
            timeSlots: [slot], // Only validate this one slot
            selectedAppointment,
            validateLocation: false,
            validateSlots: true,
            checkSlotsExist: false,
        }, t);

        const updatedErrors = { ...currentErrors };

        // Handle slot validation errors
        if (validationResult.isValid) {
            // Slot is valid - clear any existing error
            if (currentErrors.slotTimes?.[slotId]) {
                const newSlotTimes = { ...currentErrors.slotTimes };
                delete newSlotTimes[slotId];
                updatedErrors.slotTimes = Object.keys(newSlotTimes).length > 0 ? newSlotTimes : undefined;
            }
        } else {
            // Slot is invalid - set or update error
            if (validationResult.errors.slotTimes?.[slotId]) {
                updatedErrors.slotTimes = {
                    ...updatedErrors.slotTimes,
                    [slotId]: validationResult.errors.slotTimes[slotId]
                };
            } else if (currentErrors.slotTimes?.[slotId]) {
                // Validation says invalid but no specific error - clear existing error (shouldn't happen, but safety check)
                const newSlotTimes = { ...currentErrors.slotTimes };
                delete newSlotTimes[slotId];
                updatedErrors.slotTimes = Object.keys(newSlotTimes).length > 0 ? newSlotTimes : undefined;
            }
        }

        // Clear "at least one slot required" error if there's at least one complete slot
        if (hasCompleteSlot && currentErrors.slots) {
            delete updatedErrors.slots;
        }

        return updatedErrors;
    } else {
        // If slot is not complete, clear its error
        const updatedErrors = { ...currentErrors };
        if (currentErrors.slotTimes?.[slotId]) {
            const newSlotTimes = { ...currentErrors.slotTimes };
            delete newSlotTimes[slotId];
            updatedErrors.slotTimes = Object.keys(newSlotTimes).length > 0 ? newSlotTimes : undefined;
        }
        if (hasCompleteSlot && currentErrors.slots) {
            delete updatedErrors.slots;
        }
        return updatedErrors;
    }
};

export interface PrepareBodyParams {
    locationLink: string;
    locationName: string;
    instructions: string;
    timeSlots: TimeSlot[];
    applicantsPerSlot: string;
    selectedAppointment?: InterviewAppointment | null;
    useExistingSlots?: boolean;
}

export const prepareBodyForSchedule = (params: PrepareBodyParams): SaveInterviewAppointmentBody => {
    const { locationLink, locationName, instructions, timeSlots, applicantsPerSlot, selectedAppointment, useExistingSlots = false } = params;

    let slots: CreateInterviewSlotDto[];

    if (useExistingSlots && selectedAppointment?.slots) {
        // Use existing slots from appointment
        slots = selectedAppointment.slots.map(slot => {
            const slotData: CreateInterviewSlotDto = {
                startTime: slot.startTime,
                endTime: slot.endTime,
            };

            if (slot.capacity) {
                slotData.capacity = slot.capacity;
            }

            return slotData;
        });
    } else {
        // Convert time slots to the required format
        const validSlots = timeSlots.filter(slot =>
            slot.date && slot.startTime && slot.endTime
        );

        slots = validSlots.map(slot => {
            const slotData: CreateInterviewSlotDto = {
                startTime: convertToUnixTimestamp(slot.date, slot.startTime),
                endTime: convertToUnixTimestamp(slot.date, slot.endTime),
            };

            // Add capacity if applicantsPerSlot is provided
            if (applicantsPerSlot && !isNaN(Number(applicantsPerSlot))) {
                const capacity = parseInt(applicantsPerSlot, 10);
                if (capacity >= 1 && capacity <= 200) {
                    slotData.capacity = capacity;
                }
            }

            return slotData;
        });
    }

    // Prepare the request body
    // Normalize URL by adding https:// if missing
    const body: SaveInterviewAppointmentBody = {
        locationUrl: normalizeUrl(locationLink),
        slots: slots,
    };

    // Add location name if provided
    if (locationName.trim()) {
        body.locationName = locationName.trim();
    }

    // Add instructions if provided
    if (instructions.trim()) {
        body.locationInstructions = instructions.trim();
    }

    return body;
};

/**
 * Updates applicants list with new scheduled interviews based on the send invitation response.
 * 
 * @param responseData - The response data from sendInterviewInvitation API
 * @param body - The request body that was sent to the API
 * @param applicantsList - The current list of applicants from Redux state
 * @returns Updated applicants list with new scheduledInterviews added
 */
export const updateApplicantsWithScheduledInterviews = (
    responseData: SendInvitationsResponseData | undefined,
    body: SendInterviewInvitationBody,
    applicantsList: ApplicantListItem[]
): ApplicantListItem[] => {
    if (!responseData?.appointmentDetails) {
        return applicantsList;
    }

    // Get all applicant IDs that received invitations (exclude skipped ones)
    const allRequestedIds = [
        ...(body.applicationExternalIds || []),
        ...(body.offlineApplicationExternalIds || [])
    ];
    const skippedIds = new Set(responseData?.skippedApplicationIds || []);
    const invitedApplicantIds = allRequestedIds.filter(id => !skippedIds.has(id));

    if (invitedApplicantIds.length === 0) {
        return applicantsList;
    }

    const appointmentDetails = responseData.appointmentDetails;

    // Validate appointment details
    if (!appointmentDetails.externalId) {
        console.warn('updateApplicantsWithScheduledInterviews: Appointment externalId is missing');
        return applicantsList;
    }

    const sentAt = appointmentDetails.createdAt || Date.now();

    // Update applicants list with new scheduledInterviews
    return applicantsList.map((applicant: ApplicantListItem) => {
        if (!applicant?.externalId || !invitedApplicantIds.includes(applicant.externalId)) {
            return applicant;
        }

        const existingInterviews = applicant.scheduledInterviews || [];

        // Find the first available slot (prefer slots that are not full)
        // Validate that slots array exists and has items
        const slots = appointmentDetails.slots || [];
        if (slots.length === 0) {
            console.warn('updateApplicantsWithScheduledInterviews: No slots available in appointment details');
            return applicant;
        }

        const availableSlot = slots.find(slot => slot && !slot.isFull) || slots[0];

        // Validate slot has required fields
        if (!availableSlot || typeof availableSlot.startTime !== 'number' || typeof availableSlot.endTime !== 'number') {
            console.warn('updateApplicantsWithScheduledInterviews: Invalid slot data', availableSlot);
            return applicant;
        }

        // Create unique interview ID using appointment ID and applicant ID
        const interviewExternalId = `${appointmentDetails.externalId}-${applicant.externalId}`;

        // Check if this interview already exists to avoid duplicates
        const interviewExists = existingInterviews.some(
            interview => interview?.externalId === interviewExternalId
        );

        if (!interviewExists) {
            const newScheduledInterview: ScheduledInterviewDto = {
                externalId: interviewExternalId,
                status: InterviewInvitationStatusEnum.PENDING,
                locationType: appointmentDetails.locationType,
                locationName: appointmentDetails.locationName,
                sentAt: sentAt,
                slot: {
                    startTime: availableSlot.startTime,
                    endTime: availableSlot.endTime
                }
            };

            return {
                ...applicant,
                scheduledInterviews: [...existingInterviews, newScheduledInterview]
            };
        }

        return applicant;
    });
};

/**
 * Prepares applicant IDs for interview invitation (online and offline)
 * @param isBulkAction - Whether this is a bulk action
 * @param selectedApplicantsIds - Array of selected applicant IDs
 * @param selectedApplicant - Single selected applicant (if not bulk)
 * @param applicantsList - Full list of applicants
 * @returns Object containing onlineApplicantsIds and offlineApplicantsIds arrays
 */
export const prepareApplicantIdsForInvitation = (
    isBulkAction: boolean,
    selectedApplicants: ApplicantListItem[],
    selectedApplicant: ApplicantListItem | null | undefined,
): { onlineApplicantsIds: string[]; offlineApplicantsIds: string[] } => {
    const onlineApplicantsIds = isBulkAction
        ? selectedApplicants?.filter(a => !a.isOffline)?.map(a => a.externalId).filter(Boolean) || []
        : selectedApplicant?.isOffline ? [] : (selectedApplicant?.externalId ? [selectedApplicant.externalId] : []);

    const offlineApplicantsIds = isBulkAction
        ? selectedApplicants?.filter(a => a.isOffline)?.map(a => a.externalId).filter(Boolean) || []
        : selectedApplicant?.isOffline ? (selectedApplicant?.externalId ? [selectedApplicant.externalId] : []) : [];

    return { onlineApplicantsIds, offlineApplicantsIds };
};

/**
 * Calculates the total applicant count for interview invitation
 * @param isBulkAction - Whether this is a bulk action
 * @param selectedApplicantsIds - Array of selected applicant IDs
 * @returns The total count of applicants
 */
export const calculateApplicantCount = (
    isBulkAction: boolean,
    selectedApplicantsIds: string[]
): number => {
    return isBulkAction ? selectedApplicantsIds.length : 1;
};

/**
 * Generates a fallback preview message for interview invitations
 * This is used when the template from API is null or empty
 * @param locationLink - The location URL or link
 * @param instructions - Additional instructions for the interview
 * @param timeSlots - Array of time slots for the interview
 * @returns The generated preview message string
 */
export const generateInterviewPreviewMessage = (
    locationLink: string,
    instructions: string,
    timeSlots: TimeSlot[]
): string => {
    const hasLink = locationLink.trim().length > 0;
    const locationType = detectLocationTypeFromUrl(locationLink);
    const isOnline = hasLink && locationType === InterviewLocationTypeEnum.ONLINE;
    const interviewType = isOnline ? 'اونلاين' : 'حضوريه';

    const slotsText = timeSlots
        .filter(slot => slot.date && slot.startTime && slot.endTime)
        .map(slot => {
            try {
                const date = slot.date ? format(new Date(slot.date), 'dd/MM/yyyy') : '[Date]';
                return `${date} من الساعة ${slot.startTime} - ${slot.endTime}`;
            } catch {
                return `[Date] من الساعة ${slot.startTime} - ${slot.endTime}`;
            }
        })
        .join('\n');

    let message = `عزيزي المتقدم أحمد محمد\n\n`;
    message += `تمت دعوتك للمقابلة الشخصية لوظيفة كاشير في شركة ايكيا\n\n`;

    if (slotsText) {
        message += `المواعيد المتاحة:\n${slotsText}\n\n`;
    }

    if (hasLink) {
        message += `تفاصيل موقع المقابلة:\n`;
        message += `المقابلة ${interviewType} في: ${locationLink}\n\n`;
    }

    if (instructions.trim()) {
        message += `${instructions}\n\n`;
    }

    message += `الرجاء تأكيد الحضور واختيار الموعد`;

    return message;
};